import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, X, ArrowRight, GraduationCap, MapPin, Award, DollarSign, Sparkles, Calendar, BookOpen, ChevronDown, Check } from 'lucide-react';
import { getUniversitiesByCountry } from './data/universityData';
import PrivacyConsentNotice from '@/components/common/PrivacyConsentNotice';
import { LeadPhoneField } from '@/components/lead-quality/LeadPhoneField';
import { useLeadQualityEngine } from '@/components/lead-quality/useLeadQualityEngine';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Slug lookup helper — getUniversitiesByCountry returns objects with a `slug` field already
const slugOf = (uni) => (uni && uni.slug) ? uni.slug : '';

const ComparePicker = ({ allDubaiUnis, value, exclude, onChange, slot }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allDubaiUnis
      .filter((u) => !exclude.includes(u))
      .filter((u) => !q || u.name.toLowerCase().includes(q) || u.city.toLowerCase().includes(q));
  }, [allDubaiUnis, exclude, query]);

  if (value) {
    return (
      <div className="relative bg-white border-2 border-[#C5A028] rounded-2xl p-5 h-full" data-testid={`compare-slot-${slot}`}>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
          data-testid={`compare-slot-${slot}-clear`}
          aria-label="Remove"
        >
          <X className="w-4 h-4 text-slate-500" />
        </button>
        <div className="text-[10px] uppercase tracking-wider text-[#C5A028] font-bold mb-1">Slot {slot}</div>
        <h3 className="text-base font-bold font-syne text-[#003087] leading-tight mb-1">{value.name}</h3>
        <div className="flex items-center gap-1 text-xs text-slate-500">
          <MapPin className="w-3 h-3" /> {value.city}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full h-full min-h-[120px] bg-white border-2 border-dashed border-slate-300 hover:border-[#C5A028] rounded-2xl p-5 flex flex-col items-center justify-center gap-2 transition-colors group"
        data-testid={`compare-slot-${slot}-add`}
      >
        <div className="w-10 h-10 rounded-full bg-[#003087]/10 group-hover:bg-[#C5A028]/20 flex items-center justify-center transition-colors">
          <Plus className="w-5 h-5 text-[#003087] group-hover:text-[#C5A028]" />
        </div>
        <span className="text-sm font-semibold text-slate-600">Pick University {slot}</span>
        <ChevronDown className="w-3 h-3 text-slate-400" />
      </button>

      {open && (
        <div className="absolute z-30 top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden" data-testid={`compare-slot-${slot}-dropdown`}>
          <div className="p-3 border-b border-slate-100">
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Dubai universities…"
              className="w-full text-sm px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:border-[#C5A028]"
            />
          </div>
          <div className="max-h-72 overflow-y-auto sa-scrollbar">
            {filtered.length === 0 ? (
              <div className="p-4 text-xs text-slate-500 text-center">No matches</div>
            ) : filtered.map((u, i) => (
              <button
                key={u.name + i}
                type="button"
                onClick={() => { onChange(u); setOpen(false); setQuery(''); }}
                className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
              >
                <div className="text-sm font-semibold text-slate-900">{u.name}</div>
                <div className="text-[11px] text-slate-500">{u.city} · {u.type}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ icon: Icon, label, render }) => (
  <div className="grid grid-cols-4 gap-3 py-4 border-b border-slate-100 items-start" data-testid={`compare-row-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 font-bold">
      <Icon className="w-3.5 h-3.5 text-[#C5A028]" />
      <span>{label}</span>
    </div>
    {[0, 1, 2].map((i) => (
      <div key={`compare-slot-${label}-${i}`} className="text-sm text-slate-800 leading-relaxed">{render(i)}</div>
    ))}
  </div>
);

const DubaiCompareTool = () => {
  // `getUniversitiesByCountry` is a module-level import (stable); dataset is static.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allDubai = useMemo(() => getUniversitiesByCountry('dubai'), []);
  const [picks, setPicks] = useState([null, null, null]);

  const [lead, setLead] = useState({ name: '', email: '' });
  const lqe = useLeadQualityEngine();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setSlot = (i, val) => {
    const next = [...picks];
    next[i] = val;
    setPicks(next);
  };

  const exclude = picks.filter(Boolean);
  const filledCount = exclude.length;

  const slot = (i) => picks[i];

  const submitLead = async (e) => {
    e.preventDefault();
    if (filledCount === 0) return;
    const guard = await lqe.guardSubmit();
    if (!guard.ok) return;
    setSubmitting(true);
    const compareList = picks.filter(Boolean).map((u) => u.name).join(' vs ');
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: lead.name || 'Dubai Compare Lead',
          email: lead.email,
          phone: lqe.phone,
          ...guard.payloadMeta,
          service_type: 'Study Abroad - Dubai University Comparison',
          country: 'UAE',
          qualification: '',
          experience: '',
          source: 'dubai-university-compare-tool',
          message: `[Dubai University Compare Tool — dubai-university-compare-tool] Comparing: ${compareList}. ${picks.filter(Boolean).map((u) => `${u.name}: tuition ${u.averageTuition.currency} ${u.averageTuition.amount}/${u.averageTuition.period}, IELTS ${u.ieltsRequirement}, intakes ${u.intakes.join('/')}`).join(' || ')}`,
        }),
      });
      if (lqe.interpretResponse(res)) return;
      lqe.markSubmitSuccess();
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="sa-dubai-compare-tool">
      {/* Hero */}
      <section className="sa-hero-gradient">
        <div className="max-w-7xl mx-auto px-6 py-14 lg:py-18 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <Sparkles className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">Interactive Tool · 31 Dubai/UAE Universities</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold font-syne text-white mb-4">
            Compare <span className="text-[#C5A028]">Dubai Universities</span> Side-by-Side
          </h1>
          <p className="text-base text-slate-300 max-w-2xl mx-auto">
            Pick up to 3 Dubai universities and compare tuition, IELTS thresholds, intake cycles & top programs in 30 seconds.
          </p>
        </div>
      </section>

      {/* Pickers */}
      <section className="py-10 -mt-10 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <ComparePicker
                key={i}
                slot={i + 1}
                allDubaiUnis={allDubai}
                value={slot(i)}
                exclude={exclude.filter((u) => u !== slot(i))}
                onChange={(val) => setSlot(i, val)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      {filledCount >= 1 && (
        <section className="py-10 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              {/* Header row with uni names */}
              <div className="grid grid-cols-4 gap-3 py-4 px-5 bg-slate-50 border-b border-slate-100">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-bold flex items-center">Criteria</div>
                {[0, 1, 2].map((i) => (
                  <div key={i} className="text-sm font-bold font-syne text-[#003087]">
                    {slot(i) ? (
                      <Link to={`/study-abroad/universities/dubai/${slugOf(slot(i))}/`} className="hover:text-[#C5A028]">
                        {slot(i).name}
                      </Link>
                    ) : <span className="text-slate-300">—</span>}
                  </div>
                ))}
              </div>
              <div className="px-5">
                <Row icon={MapPin} label="Campus City" render={(i) => slot(i) ? slot(i).city : '—'} />
                <Row icon={Award} label="Type / Ranking" render={(i) => slot(i) ? (
                  <div>
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold mr-2 ${
                      /Public/i.test(slot(i).type) ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}>{slot(i).type}</span>
                    <div className="text-xs text-slate-600 mt-1">{slot(i).ranking}</div>
                  </div>
                ) : '—'} />
                <Row icon={DollarSign} label="Tuition (per year)" render={(i) => slot(i) ? (
                  <span><strong className="text-[#003087]">{slot(i).averageTuition.currency} {slot(i).averageTuition.amount}</strong> <span className="text-xs text-slate-500">/ {slot(i).averageTuition.period}</span></span>
                ) : '—'} />
                <Row icon={GraduationCap} label="IELTS Required" render={(i) => slot(i) ? <strong className="text-[#003087]">{slot(i).ieltsRequirement}+</strong> : '—'} />
                <Row icon={Calendar} label="Intakes" render={(i) => slot(i) ? (
                  <div className="flex flex-wrap gap-1">
                    {slot(i).intakes.map((m) => <span key={m} className="text-[11px] bg-[#003087]/5 text-[#003087] px-2 py-0.5 rounded">{m}</span>)}
                  </div>
                ) : '—'} />
                <Row icon={BookOpen} label="Top Programs" render={(i) => slot(i) ? (
                  <ul className="space-y-0.5">
                    {slot(i).popularPrograms.slice(0, 5).map((p) => (
                      <li key={p} className="text-xs text-slate-600 flex items-start gap-1">
                        <Check className="w-3 h-3 text-emerald-500 shrink-0 mt-0.5" /> {p}
                      </li>
                    ))}
                  </ul>
                ) : '—'} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Lead Capture */}
      <section className="py-14 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-3xl mx-auto px-6">
          {!submitted ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-7 lg:p-9 shadow-sm" data-testid="dubai-compare-lead-form">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#C5A028]/15 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-[#C5A028]" />
                </div>
                <div>
                  <h2 className="text-lg font-bold font-syne text-[#003087]">Get a Personalised Shortlist</h2>
                  <p className="text-xs text-slate-500">
                    {filledCount > 0 ? `Your picks (${exclude.map((u) => u.name).join(', ')}) will be sent to our Dubai-region advisor.` : 'Tell our Dubai-region advisor what you\'re looking for.'}
                  </p>
                </div>
              </div>
              <form onSubmit={submitLead} className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-start">
                <input
                  type="text" required value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })}
                  placeholder="Your Name" className="sa-input" data-testid="dubai-compare-name"
                />
                <input
                  type="email" required value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })}
                  placeholder="Email" className="sa-input" data-testid="dubai-compare-email"
                />
                <LeadPhoneField engine={lqe} testId="dubai-compare-phone" />
                <div className="sm:col-span-3">
                  <PrivacyConsentNotice tone="light" testid="dubai-compare-consent" />
                </div>
                <button
                  type="submit" disabled={submitting}
                  className="sm:col-span-3 inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm py-3.5 rounded-full transition-all disabled:opacity-60"
                  data-testid="dubai-compare-submit"
                >
                  {submitting ? 'Sending…' : (<>Get My Shortlist Now <ArrowRight className="w-4 h-4" /></>)}
                </button>
                <p className="sm:col-span-3 text-[10px] text-slate-400 text-center">
                  Lead is tagged <code className="bg-slate-100 px-1.5 py-0.5 rounded">dubai-university-compare-tool</code> in CRM.
                </p>
              </form>
            </div>
          ) : (
            <div className="bg-white border border-emerald-200 rounded-2xl p-9 text-center" data-testid="dubai-compare-success">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold font-syne text-[#003087] mb-1">Got it — we&apos;ll be in touch</h3>
              <p className="text-sm text-slate-500 mb-5">
                A ZAFCO Dubai-region advisor will reach out within 24 hours with your comparison shortlist + tuition fee details.
              </p>
              <Link to="/study-abroad/" className="text-sm font-semibold text-[#003087] hover:text-[#C5A028]">
                ← Back to Study Abroad
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default DubaiCompareTool;
